/**
 * guestChat client — frontend caller for the G1 guest Prest0n chat turn
 * (`guestRunChatTurn`), plus the guest device-id + "has signed in before"
 * device-memory helpers the sign-in charter branches on.
 *
 * URL pattern mirrors guestServerResearch.ts (direct Cloud Function URL —
 * bypasses Firebase Hosting's 60s rewrite timeout; the turn can legitimately
 * run up to the server's 45s model budget).
 *
 * STORAGE KEYS ARE DELIBERATELY OUTSIDE EVERY PURGED NAMESPACE: the session
 * reset sweep (threadPersist.purgeThreadStorageForSessionReset + the
 * lib/auth/session.ts residue list) removes prest0_threads__* /
 * prest0_thread_msgs__* / prest0_global_chat_history etc. — neither key below
 * shares those prefixes, so guest identity + device memory survive sign-out
 * by design (owner decision 2026-07-14: device memory drives conditional vs
 * direct sign-in phrasing).
 */

// Same direct-URL base as guestServerResearch.ts (GUEST_START_URL style).
const FUNCTIONS_BASE = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';
export const GUEST_CHAT_TURN_URL = `${FUNCTIONS_BASE}/guestRunChatTurn`;

export const GUEST_DEVICE_ID_STORAGE_KEY = 'prest0_guest_device_id';
export const HAS_SIGNED_IN_STORAGE_KEY = 'prest0_has_signed_in';

export interface GuestChatHistoryEntry {
  role: 'user' | 'assistant';
  text: string;
}

export interface GuestChatTurnParams {
  message: string;
  history: GuestChatHistoryEntry[];
  route: string;
  deviceId: string;
  hasSignedInBefore: boolean;
  clientTurnId?: string;
  signal?: AbortSignal;
}

export type GuestChatTurnResponse =
  | { ok: true; reply: string; model?: string; clientTurnId?: string }
  | { ok: false; reason: string; message?: string; retryAfterSec?: number };

/** Client-side mirror of the server history caps (server re-enforces). */
export const GUEST_CHAT_HISTORY_MAX_ENTRIES = 12;
export const GUEST_CHAT_HISTORY_MAX_TOTAL_CHARS = 8000;

// In-memory fallback when localStorage is unavailable (private-mode edge
// cases) — a stable id per page load is still better than none.
let ephemeralDeviceId: string | null = null;

function mintDeviceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `dev_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

/** Mint + persist the opaque guest device id ('prest0_guest_device_id'). */
export function getGuestDeviceId(): string {
  try {
    const existing = localStorage.getItem(GUEST_DEVICE_ID_STORAGE_KEY);
    if (existing && existing.trim()) return existing.trim();
    const minted = mintDeviceId();
    localStorage.setItem(GUEST_DEVICE_ID_STORAGE_KEY, minted);
    return minted;
  } catch {
    if (!ephemeralDeviceId) ephemeralDeviceId = mintDeviceId();
    return ephemeralDeviceId;
  }
}

/** True when this DEVICE has ever completed a sign-in (survives sign-out). */
export function hasSignedInBeforeMarker(): boolean {
  try {
    return localStorage.getItem(HAS_SIGNED_IN_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Set on every successful (non-anonymous) sign-in — see lib/auth/session.ts. */
export function markHasSignedIn(): void {
  try {
    localStorage.setItem(HAS_SIGNED_IN_STORAGE_KEY, '1');
  } catch {
    /* device memory is best-effort */
  }
}

// ── D13-5 visitor inheritance claim (owner ruling 2026-08-24) ───────────────
//
// On sign-in, the device asks the server to inherit any pre-account visitor
// memory (visitors/{ipHash}, server-only) into this account — presenting the
// long-lived guest deviceId as the join (it survives sign-out by design,
// while the network IP often changes between guest browsing and signup).
// The server is first-claim-wins and idempotent; this caller's only job is
// not to hammer: a per-uid marker latches ONLY after something was actually
// claimed and fully wiped, so a device with nothing stored simply re-asks on
// the (rare) next sign-in — which also catches guest data formed later.
// Marker prefix is deliberately OUTSIDE every purged namespace (see the
// header note above).

export const VISITOR_INHERITANCE_URL = `${FUNCTIONS_BASE}/claimVisitorInheritance`;
export const VISITOR_INHERIT_MARKER_PREFIX = 'prest0_visitor_inherit__u_';

export function visitorInheritMarkerKey(uid: string): string {
  return `${VISITOR_INHERIT_MARKER_PREFIX}${uid}`;
}

/** Pure decision: attempt a claim on this sign-in? (Marker not yet latched.) */
export function shouldAttemptVisitorInheritance(uid: string): boolean {
  try {
    return localStorage.getItem(visitorInheritMarkerKey(uid)) !== 'done';
  } catch {
    // No storage → no marker discipline → skip rather than call on every
    // auth event (the server would tolerate it, but there's no way to stop).
    return false;
  }
}

/**
 * Fire-and-forget claim (called from lib/auth/session.ts on non-anonymous
 * sign-in). Never throws; every failure path simply leaves the marker
 * unlatched so the next sign-in retries.
 */
export async function claimVisitorInheritanceOnce(user: {
  uid: string;
  getIdToken(): Promise<string>;
}): Promise<void> {
  if (!user?.uid || !shouldAttemptVisitorInheritance(user.uid)) return;
  try {
    const token = await user.getIdToken();
    const response = await fetch(VISITOR_INHERITANCE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ deviceId: getGuestDeviceId() }),
    });
    if (!response.ok) return;
    const body: any = await response.json().catch(() => null);
    // Latch only a REAL completed inheritance: something claimed AND the
    // visitor copy fully wiped. 'disabled', empty-store, and partial wipes
    // all stay unlatched (retry on a later sign-in).
    if (body?.ok === true && body?.complete === true && Number(body?.claimed) > 0) {
      try {
        localStorage.setItem(visitorInheritMarkerKey(user.uid), 'done');
      } catch {
        /* best-effort */
      }
    }
  } catch {
    /* best-effort — retries next sign-in */
  }
}

/**
 * Build the guest-turn history from a thread's message list: user/assistant
 * turns only (welcome bubbles, notices, pending widgets, and every other
 * tagged system message are stripped — guest replies keep their own
 * 'guest_chat_reply' tag and ARE conversation content), excluding the
 * just-sent user bubble for `currentText`, capped to the last 12 entries and
 * ~8k chars (newest kept).
 */
export function buildGuestChatHistory(
  messages: Array<{ isUser?: boolean; text?: string; sourceTag?: string }>,
  currentText: string,
): GuestChatHistoryEntry[] {
  const eligible: GuestChatHistoryEntry[] = [];
  for (const m of messages || []) {
    const text = (m?.text || '').trim();
    if (!text) continue;
    if (m.isUser) {
      if (m.sourceTag) continue; // hidden/system-minted user turns
      eligible.push({ role: 'user', text });
    } else {
      if (m.sourceTag && m.sourceTag !== 'guest_chat_reply') continue;
      eligible.push({ role: 'assistant', text });
    }
  }
  // The envelope already appended THIS turn's user bubble to the thread —
  // it rides the request's `message` field, not the history.
  const last = eligible[eligible.length - 1];
  if (last && last.role === 'user' && last.text === currentText.trim()) {
    eligible.pop();
  }
  const lastN = eligible.slice(-GUEST_CHAT_HISTORY_MAX_ENTRIES);
  const kept: GuestChatHistoryEntry[] = [];
  let total = 0;
  for (let i = lastN.length - 1; i >= 0; i -= 1) {
    total += lastN[i].text.length;
    if (total > GUEST_CHAT_HISTORY_MAX_TOTAL_CHARS) break;
    kept.unshift(lastN[i]);
  }
  return kept;
}

/** How long a guest turn waits for its arrival note before going without it. */
export const GUEST_ARRIVAL_WAIT_MS = 1500;

/**
 * How this guest first arrived (src/services/acquisition/arrival.ts; the Sales Funnel, 2026-11-03), for the visitor
 * profile's first stored turn. Loaded on demand and never waited on long: a turn goes without it rather than wait.
 */
async function arrivalForTurn(): Promise<unknown> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    const load = import('../acquisition/arrival').then((m) => m.arrivalForGuestTurn()).catch(() => undefined);
    const late = new Promise<undefined>((resolve) => {
      timer = setTimeout(() => resolve(undefined), GUEST_ARRIVAL_WAIT_MS);
    });
    return await Promise.race([load, late]);
  } catch {
    return undefined;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Run one guest chat turn. Resolves with the parsed body for BOTH success and
 * friendly-rejection shapes ({ok:false, reason, message}); throws only on
 * transport failures / unparseable responses.
 */
export async function callGuestChatTurn(
  params: GuestChatTurnParams,
): Promise<GuestChatTurnResponse> {
  const arrival = await arrivalForTurn();
  const res = await fetch(GUEST_CHAT_TURN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: params.message,
      history: params.history,
      route: params.route,
      deviceId: params.deviceId,
      hasSignedInBefore: params.hasSignedInBefore,
      ...(params.clientTurnId ? { clientTurnId: params.clientTurnId } : {}),
      ...(arrival ? { arrival } : {}),
    }),
    signal: params.signal,
  });

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    throw new Error(`guest chat turn failed (${res.status})`);
  }

  const b = (body || {}) as Record<string, unknown>;
  if (b.ok === true && typeof b.reply === 'string' && b.reply.trim()) {
    return {
      ok: true,
      reply: b.reply,
      ...(typeof b.model === 'string' ? { model: b.model } : {}),
      ...(typeof b.clientTurnId === 'string' ? { clientTurnId: b.clientTurnId } : {}),
    };
  }
  if (b.ok === false) {
    return {
      ok: false,
      reason: typeof b.reason === 'string' ? b.reason : 'unknown',
      ...(typeof b.message === 'string' ? { message: b.message } : {}),
      ...(typeof b.retryAfterSec === 'number' ? { retryAfterSec: b.retryAfterSec } : {}),
    };
  }
  throw new Error(`guest chat turn returned an unexpected body (${res.status})`);
}

// ── forget me (the no-account half of the data rights) ──────────────────────

export const FORGET_GUEST_CONVERSATION_URL = `${FUNCTIONS_BASE}/forgetGuestConversation`;

export type ForgetGuestResult =
  | { ok: true; forgotten: true; deletedRows: number }
  | { ok: true; partial: true; deletedRows: number; message: string }
  | { ok: true; nothingToForget: true }
  | { ok: false; reason: string; message?: string; retryAfterSec?: number };

/**
 * Ask us to forget this visitor's conversation now, rather than at the 30-day
 * mark it expires on anyway.
 *
 * CARRIES NOTHING. Not the device id, not a visitor id, not a hash — the
 * server derives the subject from the connection the request arrives on, so
 * there is no parameter here that could name somebody else's conversation.
 * That is what makes an unauthenticated destructive endpoint safe, and it is
 * why this function takes no arguments beyond a test seam. Do not add one.
 */
export async function forgetGuestConversation(
  fetchImpl: typeof fetch = fetch,
): Promise<ForgetGuestResult> {
  let res: Response;
  try {
    res = await fetchImpl(FORGET_GUEST_CONVERSATION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch {
    return { ok: false, reason: 'network', message: 'could not reach us. try again?' };
  }
  try {
    return (await res.json()) as ForgetGuestResult;
  } catch {
    return { ok: false, reason: 'malformed', message: 'we sent something unreadable back.' };
  }
}
